/**
 * @module chartConfigs/nullHandling/applyNullUiToLineSeries
 * @description 按 null_ui 策略变换单条折线 series（data + connectNulls）
 */

import type { TNullUiValue } from '@dbl/core/chartEditor';
import type { ISeriesItem } from '../seriesUtils';
import type { TSeriesData } from '../types';
import { isNullDataPoint } from './isNullDataPoint';

const mapNullPointsToZero = (data: TSeriesData[]): TSeriesData[] => {
  return data.map((item) => {
    if (!isNullDataPoint(item?.value)) {
      return item;
    }
    return {
      ...item,
      value: 0,
      rawValue: 0,
    };
  });
};

/**
 * 对单条折线 series 应用空值展示策略。
 * - 0：空值映射为 0，connectNulls=false
 * - ignore：保持 data，connectNulls=true（跨空值连线；调用方应跳过 setAloneSymbolOpacity，
 *   否则折线上会出现部分圆点显示(原本孤立的点)、部分不显示的混杂效果）
 * - break（含缺省/非法值）：保持 data，connectNulls=false
 */
export const applyNullUiToLineSeries = <T extends ISeriesItem>(
  seriesItem: T,
  nullUi: TNullUiValue | string | undefined,
): T => {
  if (nullUi === '0') {
    return {
      ...seriesItem,
      data: mapNullPointsToZero(seriesItem.data ?? []),
      connectNulls: false,
    };
  }

  if (nullUi === 'ignore') {
    return {
      ...seriesItem,
      connectNulls: true,
    };
  }

  return {
    ...seriesItem,
    connectNulls: false,
  };
};
